import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import type {
  DayOfWeek,
  ItemCategory,
  ItemImage,
  ListingSource,
} from "@/lib/types";
import type { ListingFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import { completeSellListingImages } from "@/lib/listing/completeSellListingImages";
import { assertSellListingImagesReady } from "@/lib/listing/sellListingValidation";
import type { ResolvedLocationIds } from "@/lib/locationIds";
import { supabase as browserSupabase } from "@/lib/supabase";
import { SELLER_PRICE_BAND_ERROR_CODES } from "@/lib/pricing/sellerPriceBandErrors";
import { LISTING_TITLE_MAX_LENGTH } from "@/lib/listing/chairListingSpec";
import {
  computeListingImageContentHashFromBlob,
  DUPLICATE_LISTING_PHOTOS_ERROR_CODE,
  ListingPhotoDuplicateError,
} from "@/lib/listing/listingPhotoDuplicateCheck";

export interface CreateSellListingFormPayload {
  title: string;
  description: string;
  defects: string;
  price: string;
  category: ItemCategory;
  subcategory: string;
  location: string;
  quantity: number;
  brand?: string;
  model?: string;
  size?: string;
  familySpecs?: ListingFamilySpecs;
}

export interface CreateSellListingParams {
  supabase: SupabaseClient<Database>;
  userId: string;
  itemId: string;
  formData: CreateSellListingFormPayload;
  pickupDays: DayOfWeek[];
  pickupStartHour: string;
  pickupEndHour: string;
  uploadedImages: ItemImage[];
  /** Shared across items in one bulk submit run; null for single-item listings */
  listingBatchId?: string | null;
  listingSource?: ListingSource;
  locationIds?: ResolvedLocationIds | null;
}

type SellListingApiSuccess = {
  itemId: string;
  persistedTitle?: string;
  warningCode?: string;
};

type SellListingApiError = {
  error: string;
  code?: string;
  band?: { min: number; max: number; sampleCount: number };
};

/**
 * Creates one listing via POST /api/sell/listings (server policy + insert),
 * then uploads images on the client.
 * On image failure after the item row exists, cleanup removes pickup + item.
 */
export async function createSellListing(
  params: CreateSellListingParams,
): Promise<{ itemId: string }> {
  assertSellListingImagesReady(params.uploadedImages);

  const photoContentHashes = await Promise.all(
    params.uploadedImages.map((image) => {
      if (!image.file) {
        throw new Error(
          "One or more images are invalid. Please try uploading them again.",
        );
      }
      return computeListingImageContentHashFromBlob(image.file);
    }),
  );

  const {
    data: { session },
  } = await browserSupabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("You must be signed in to create a listing.");
  }

  const response = await fetch("/api/sell/listings", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      itemId: params.itemId,
      formData: params.formData,
      pickupDays: params.pickupDays,
      pickupStartHour: params.pickupStartHour,
      pickupEndHour: params.pickupEndHour,
      listingBatchId: params.listingBatchId ?? null,
      listingSource: params.listingSource ?? "single",
      bandTitle: params.formData.title,
      locationIds: params.locationIds ?? null,
      photoContentHashes,
    }),
  });

  const payload = (await response.json().catch(() => ({}))) as
    | SellListingApiSuccess
    | SellListingApiError;

  if (!response.ok) {
    const err = payload as SellListingApiError;
    if (err.code === DUPLICATE_LISTING_PHOTOS_ERROR_CODE) {
      throw new ListingPhotoDuplicateError(
        err.error || "Use three different photos of the item.",
      );
    }
    if (err.code === SELLER_PRICE_BAND_ERROR_CODES.PRICE_OUT_OF_BAND) {
      // Opaque copy only — do not leak band.min/max into the toast (teaches ceiling fishing).
      throw new Error(
        err.error ||
          "That price is higher than similar items usually sell for.",
      );
    }
    throw new Error(err.error || "Failed to create listing");
  }

  const success = payload as SellListingApiSuccess;
  const itemId = success.itemId ?? params.itemId;
  const persistedTitle =
    success.persistedTitle ??
    params.formData.title.trim().slice(0, LISTING_TITLE_MAX_LENGTH);
  const numericPrice = Number(params.formData.price.replace(/,/g, ""));

  await completeSellListingImages({
    supabase: params.supabase,
    itemId,
    persistedTitle,
    numericPrice,
    uploadedImages: params.uploadedImages,
    listingBatchId: params.listingBatchId ?? null,
  });

  return { itemId };
}
